// ChargeCard — one invoice charge inside RecordInvoiceModal's step 2 (confirm):
// a static presentation (a payable's agreed-price pill, or a manual charge's
// editable Description input), the qty × rate control for a rated product
// charge, the per-line splits editor (SplitRow list + add/remove — each row
// gets a row-quantity input, seeded from splitQtyDefault, when the charge is
// rated and has more than one split), and the axis-2 price footer. Extracted
// so the modal stays holdable. Presentational: every edit is reported up via
// the on* callbacks that the modal wires to the pure invoiceFormState
// transforms.
//
// Charges arrive pre-seeded — a payable-backed card from step 1's catalogue
// tick (via chargeFromPayable, reconciled by reconcileSelection) or a manual
// card from the "Add manual charge" action — so this card no longer offers a
// picker to change which payable it covers. A prominent agreed-price highlight
// surfaces the payable's snapshotted price (or "— variable" when there is no
// price signal) for reconciliation, and the axis-2 footer runs
// validatePrice(chargeSum, expectedAmount) — staying silent for a
// variable/contingency charge that has no agreed price.
//
// Above the splits list, a per-line notice (duplicateSplitLineIds) flags any
// PO line this charge draws more than once, with a Combine button that merges
// those splits into one row (onCombineLine). `highlightSplit` passes through
// to the matching SplitRow — the modal's qty<->amount sync (applySplitSync)
// sets it briefly so a silent money change stays visible.

import { Plus, Trash2 } from "lucide-react";
import Button from "../../common/Button";
import NumberInput from "../../common/NumberInput";
import SplitRow from "./SplitRow";
import { validatePrice, formatAud } from "../../../utils/financeCalc";
import {
  chargeSum,
  splitQtyDefault,
  duplicateSplitLineIds,
  type ChargeDraft,
  type SplitDraft,
  type LineContention,
} from "./invoiceFormState";
import type { PoLine, LineBalance } from "../../../types/finance";

interface ChargeCardProps {
  charge: ChargeDraft;
  index: number;
  poLines: PoLine[];
  lineBalances: Record<string, LineBalance>;
  contentionByLine: Record<string, LineContention>;
  chargeError?: string;
  splitErrors: Record<string, string>;
  /** Set when the modal's qty<->amount sync just auto-adjusted a split in
   *  this charge — forwarded to the matching SplitRow by key. */
  highlightSplit?: { splitKey: string; field: "amount" | "qty" } | null;
  onPatchCharge: (patch: Partial<ChargeDraft>) => void;
  onPatchSplit: (splitIndex: number, patch: Partial<SplitDraft>) => void;
  onAddSplit: () => void;
  onRemoveSplit: (splitIndex: number) => void;
  onRemoveCharge: () => void;
  canRemoveCharge: boolean;
  /** Merge this charge's same-line splits into a single row (Combine). */
  onCombineLine: (poLineId: string) => void;
  disabled?: boolean;
}

// validatePrice only ever returns ok / over / under / none; the footer renders
// only when kind !== "none", so these three keys cover every rendered case (no
// fallback arm needed — the index is always one of these at runtime).
const PRICE_CLASS: Record<string, string> = {
  ok: "text-emerald-700 dark:text-emerald-400",
  over: "text-red-600 dark:text-red-400",
  under: "text-amber-700 dark:text-amber-400",
};

export default function ChargeCard({
  charge,
  index,
  poLines,
  lineBalances,
  contentionByLine,
  chargeError,
  splitErrors,
  highlightSplit,
  onPatchCharge,
  onPatchSplit,
  onAddSplit,
  onRemoveSplit,
  onRemoveCharge,
  canRemoveCharge,
  onCombineLine,
  disabled,
}: ChargeCardProps) {
  const labelId = `${charge.key}-label`;
  const qtyId = `${charge.key}-qty`;
  const isManual = !charge.payableId;
  const isRated = charge.rate !== undefined && charge.rate !== null;

  const sum = chargeSum(charge);
  const price = validatePrice(sum, charge.expectedAmount);

  return (
    <div
      data-testid={`invoice-charge-card-${index}`}
      className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-900/40"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {isManual ? (
            <div>
              <span className="text-overline uppercase text-slate-500 dark:text-slate-400">
                Manual charge
              </span>
              <label htmlFor={labelId} className="label mt-1">
                Description
              </label>
              <input
                id={labelId}
                type="text"
                aria-label="Charge description"
                className="input-sm w-full"
                value={charge.label}
                onChange={(e) => onPatchCharge({ label: e.target.value })}
                disabled={disabled}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <span className="block text-body-sm font-medium text-slate-900 dark:text-slate-100">
                {charge.label}
              </span>
              <div
                data-testid={`charge-agreed-${index}`}
                className="inline-flex items-baseline gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-2 py-1 dark:border-brand-900 dark:bg-brand-950/40"
              >
                <span className="text-overline uppercase text-brand-700 dark:text-brand-300">
                  Agreed price
                </span>
                <span className="text-body-sm font-semibold tabular-nums text-brand-700 dark:text-brand-300">
                  {charge.expectedAmount !== null
                    ? formatAud(charge.expectedAmount)
                    : "— variable"}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-start pt-0.5">
          {canRemoveCharge && (
            <button
              type="button"
              aria-label="Remove charge"
              onClick={onRemoveCharge}
              disabled={disabled}
              className="btn-icon-ghost btn-icon-sm text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {isRated && (
        <div className="mt-2 flex items-end gap-2">
          <div className="w-28">
            <label htmlFor={qtyId} className="label">
              Quantity
            </label>
            <NumberInput
              id={qtyId}
              size="sm"
              ariaLabel="Quantity"
              className="w-full"
              value={charge.qty ?? ""}
              onChange={(raw) => onPatchCharge({ qty: raw })}
              disabled={disabled}
            />
          </div>
          <span className="pb-2 text-caption text-slate-500 dark:text-slate-400">
            × {formatAud(charge.rate as number)}
            {charge.unit ? ` / ${charge.unit}` : ""}
          </span>
        </div>
      )}

      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-overline uppercase text-slate-500 dark:text-slate-400">
            Allocations
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
            onClick={onAddSplit}
            disabled={disabled}
          >
            Add split
          </Button>
        </div>
        {chargeError && (
          <p className="text-caption text-red-600 dark:text-red-400">{chargeError}</p>
        )}
        {duplicateSplitLineIds(charge).map((lineId) => {
          const line = poLines.find((l) => l.id === lineId);
          const count = charge.splits.filter((s) => s.poLineId === lineId).length;
          return (
            <p
              key={lineId}
              className="flex flex-wrap items-center gap-2 text-caption text-amber-700 dark:text-amber-400"
            >
              <span>
                {count} allocations target the same PO line
                {line ? ` (${line.lineNumber} — ${line.description})` : ""}.
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onCombineLine(lineId)}
                disabled={disabled}
              >
                Combine
              </Button>
            </p>
          );
        })}
        {charge.splits.map((split, splitIndex) => (
          <SplitRow
            key={split.key}
            split={split}
            poLines={poLines}
            lineBalances={lineBalances}
            contention={contentionByLine[split.poLineId]}
            error={splitErrors[split.key]}
            chargeRate={charge.rate}
            chargeUnit={charge.unit}
            showQty={isRated && charge.splits.length > 1}
            qtyDisplay={split.qty !== "" ? split.qty : splitQtyDefault(charge, split)}
            highlightField={
              highlightSplit?.splitKey === split.key ? highlightSplit.field : null
            }
            onPatch={(patch) => onPatchSplit(splitIndex, patch)}
            onRemove={() => onRemoveSplit(splitIndex)}
            canRemove={charge.splits.length > 1}
            disabled={disabled}
          />
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2 border-t border-slate-200 pt-2 dark:border-slate-700">
        <span className="text-caption text-slate-500 dark:text-slate-400">
          Charge total{" "}
          <span className="tabular-nums text-slate-700 dark:text-slate-300">
            {formatAud(sum)}
          </span>
        </span>
        {price.kind !== "none" && (
          <span
            data-testid={`charge-price-${index}`}
            className={`text-caption font-medium ${PRICE_CLASS[price.kind]}`}
          >
            {price.text}
          </span>
        )}
      </div>
    </div>
  );
}
